import { z } from 'zod';
import { ClickAction } from '../enums/click-action';

export interface PushNotificationData {
  'pinpoint.openApp'?: string;
  'pinpoint.notification.silentPush'?: string;
  'pinpoint.campaign.campaign_id'?: string;
  'pinpoint.notification.body'?: string;
  'pinpoint.notification.title'?: string;
  'pinpoint.jsonBody'?: string;
  'pinpoint.notification.imageIconUrl'?: string;
}

export const VehicleParamsSchema = z.object({
  vehicle_id: z.string(),
  plate_number: z.string(),
  imei: z.string(),
});

export const JobParamsSchema = z.object({
  job_id: z.string(),
  job_number: z.string(),
});

export const LoadParamsSchema = z.object({
  job_id: z.string(),
  load_id: z.string(),
  load_number: z.string(),
});

export const QuoteParamsSchema = JobParamsSchema.merge(LoadParamsSchema).extend({
  quote_id: z.string(),
});

export const PaymentParamsSchema = JobParamsSchema.extend({
  payment_id: z.string(),
});

export const PromotionParamsSchema = z.object({
  promotion_id: z.string(),
  promotion_type: z.string(),
});

export const SystemAlertParamsSchema = z.object({
  alert_id: z.string(),
  alert_title: z.string(),
  alert_type: z.string(),
});

export const NewShipperSignupParamsSchema = z.object({
  shipper_sub: z.string(),
  shipper_name: z.string(),
});

export const MaintenanceDueParamsSchema = z.object({
  vehicle_id: z.string(),
  plate_number: z.string(),
  maintenance_id: z.string(),
});

export const DeviceExpiryParamsSchema = z.object({
  device_id: z.string(),
  imei: z.string(),
});

export const LicenseExpiryParamsSchema = z.object({
  driver_id: z.string(),
  license_number: z.string(),
  driver_name: z.string(),
});

export const CarrierDeactivatedParamsSchema = z.object({
  carrier_id: z.string(),
  parent_sub: z.string(),
});

export const AssignedVehicleParamsSchema = z.object({
  vehicle_id: z.string(),
  plate_number: z.string(),
});

export const VettingParamsSchema = z.object({
  shipper_id: z.string(),
  shipper_sub: z.string(),
});

export const GeofenceParamsSchema = VehicleParamsSchema.extend({
  geofence_zone_num: z.string(),
});

export const TermsAndConditionsUpdatedParamsSchema = z.object({
  effective_date: z.string(),
  latest_terms_hash: z.string(),
});

export type VehicleParams = z.infer<typeof VehicleParamsSchema>;
export type JobParams = z.infer<typeof JobParamsSchema>;
export type LoadParams = z.infer<typeof LoadParamsSchema>;
export type QuoteParams = z.infer<typeof QuoteParamsSchema>;
export type PaymentParams = z.infer<typeof PaymentParamsSchema>;
export type PromotionPrams = z.infer<typeof PromotionParamsSchema>;
export type SystemAlertParams = z.infer<typeof SystemAlertParamsSchema>;
export type NewShipperSignupParams = z.infer<typeof NewShipperSignupParamsSchema>;
export type AssignedVehicleParams = z.infer<typeof AssignedVehicleParamsSchema>;
export type VettingParams = z.infer<typeof VettingParamsSchema>;
export type TermsAndConditionsUpdatedParams = z.infer<
  typeof TermsAndConditionsUpdatedParamsSchema
>;

export const ClickActionSchemaMap = {
  [ClickAction.PROMOTION_NOTIFICATION_TARGET]: PromotionParamsSchema,
  [ClickAction.SYSTEM_ALERT_NOTIFICATION_TARGET]: SystemAlertParamsSchema,
  [ClickAction.ADMIN_NEW_SHIPPER_SIGNUP_NOTIFICATION_TARGET]:
    NewShipperSignupParamsSchema,
  [ClickAction.ADMIN_LOAD_CLEARANCE_NOTIFICATION_TARGET]: QuoteParamsSchema,
  [ClickAction.ADMIN_JOB_APPROVAL_NOTIFICATION_TARGET]: JobParamsSchema,
  [ClickAction.ADMIN_PAYMENT_RECEIVED_NOTIFICATION_TARGET]: PaymentParamsSchema,
  [ClickAction.ADMIN_PAYMENT_DUE_EXPIRING_NOTIFICATION_TARGET]:
    PaymentParamsSchema,
  [ClickAction.OVERSPEED_NOTIFICATION_TARGET]: VehicleParamsSchema,
  [ClickAction.HARSH_ACCELERATION_NOTIFICATION_TARGET]: VehicleParamsSchema,
  [ClickAction.HARSH_BRAKING_NOTIFICATION_TARGET]: VehicleParamsSchema,
  [ClickAction.SHARP_TURN_NOTIFICATION_TARGET]: VehicleParamsSchema,
  [ClickAction.FUEL_DROP_NOTIFICATION_TARGET]: VehicleParamsSchema,
  [ClickAction.FUEL_RISE_NOTIFICATION_TARGET]: VehicleParamsSchema,
  [ClickAction.ENGINE_OVERHEAT_NOTIFICATION_TARGET]: VehicleParamsSchema,
  [ClickAction.GEOFENCE_EXIT_NOTIFICATION_TARGET]: GeofenceParamsSchema,
  [ClickAction.GEOFENCE_ENTRY_NOTIFICATION_TARGET]: GeofenceParamsSchema,
  [ClickAction.MAINTENANCE_DUE_NOTIFICATION_TARGET]: MaintenanceDueParamsSchema,
  [ClickAction.DEVICE_EXPIRY_NOTIFICATION_TARGET]: DeviceExpiryParamsSchema,
  [ClickAction.LICENSE_EXPIRY_NOTIFICATION_TARGET]: LicenseExpiryParamsSchema,
  [ClickAction.VEHICLE_OFFLINE_NOTIFICATION_TARGET]: VehicleParamsSchema,
  [ClickAction.LOW_BATTERY_NOTIFICATION_TARGET]: VehicleParamsSchema,
  [ClickAction.TRACKER_DISCONNECTION_NOTIFICATION_TARGET]: VehicleParamsSchema,
  [ClickAction.TRACKER_BATTERY_LOW_NOTIFICATION_TARGET]: VehicleParamsSchema,
  [ClickAction.NEW_JOB_NOTIFICATION_TARGET]: JobParamsSchema.extend({
    vehicle_id: z.string(),
    plate_number: z.string(),
  }),
  [ClickAction.OUTBID_NOTIFICATION_TARGET]: QuoteParamsSchema,
  [ClickAction.CARRIER_DEACTIVATED_NOTIFICATION_TARGET]:
    CarrierDeactivatedParamsSchema,
  [ClickAction.CARRIER_COUNTER_OFFER_NOTIFICATION_TARGET]: QuoteParamsSchema,
  [ClickAction.OFFER_ACCEPTED_CARRIER_NOTIFICATION_TARGET]: QuoteParamsSchema,
  [ClickAction.ADMIN_BID_APPROVAL_NOTIFICATION_TARGET]: QuoteParamsSchema,
  [ClickAction.ADMIN_BID_REJECTION_NOTIFICATION_TARGET]: QuoteParamsSchema,
  [ClickAction.BID_LOAD_MODIFIED_NOTIFICATION_TARGET]: JobParamsSchema,
  [ClickAction.PICKUP_DATE_APPROACHING_NOTIFICATION_TARGET]: QuoteParamsSchema,
  [ClickAction.PAYMENT_RECEIVED_APPROVAL_NOTIFICATION_TARGET]: QuoteParamsSchema,
  [ClickAction.QUOTES_NOTIFICATION_TARGET]: QuoteParamsSchema,
  [ClickAction.ASSIGNED_VEHICLE_NOTIFICATION_TARGET]:
    AssignedVehicleParamsSchema,
  [ClickAction.LOAD_PROGRESS_UPDATE_NOTIFICATION_TARGET]: LoadParamsSchema,
  [ClickAction.LOAD_DELIVERED_NOTIFICATION_TARGET]: LoadParamsSchema,
  [ClickAction.ADMIN_LOAD_COMPLETION_APPROVAL_TARGET]: LoadParamsSchema,
  [ClickAction.LOAD_COMPLETED_NOTIFICATION_TARGET]: LoadParamsSchema,
  [ClickAction.VETTING_SUCCESSFUL_NOTIFICATION_TARGET]: VettingParamsSchema,
  [ClickAction.VETTING_FAILED_NOTIFICATION_TARGET]: VettingParamsSchema,
  [ClickAction.SHIPPER_COUNTER_OFFER_NOTIFICATION_TARGET]: QuoteParamsSchema,
  [ClickAction.OFFER_ACCEPTED_SHIPPER_NOTIFICATION_TARGET]: QuoteParamsSchema,
  [ClickAction.ADMIN_BID_APPROVAL_SHIPPER_NOTIFICATION_TARGET]:
    QuoteParamsSchema,
  [ClickAction.ADMIN_REJECTED_ACCEPTED_LOAD_NOTIFICATION_TARGET]:
    QuoteParamsSchema,
  [ClickAction.SHIPPER_JOB_APPROVED_NOTIFICATION_TARGET]: JobParamsSchema,
  [ClickAction.JOB_EXPIRED_NOTIFICATION_TARGET]: JobParamsSchema,
  [ClickAction.PAYMENT_OVERDUE_NOTIFICATION_TARGET]: PaymentParamsSchema,
  [ClickAction.TERMS_AND_CONDITIONS_UPDATED_NOTIFICATION_TARGET]:
    TermsAndConditionsUpdatedParamsSchema,
} satisfies Record<ClickAction, z.ZodTypeAny>;

export type ClickActionKey = keyof typeof ClickActionSchemaMap;

export type ClickActionParams<T extends ClickActionKey> = z.infer<
  (typeof ClickActionSchemaMap)[T]
>;

export type DeepLinkPayload<T extends ClickActionKey = ClickActionKey> = {
  click_action: T;
  params: ClickActionParams<T>;
};
